import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Check, Wrench, Clock, AlertCircle } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const BUILD_TYPES = [
  "Single modification",
  "Stage 1 / 2 / 3 build",
  "Full performance build",
  "Suspension / brakes overhaul",
  "Track-day prep",
  "Pre-purchase consultation",
  "Other",
];

const TIME_SLOTS = [
  { value: "morning", label: "Morning (9am – 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm – 3pm)" },
  { value: "evening", label: "Late afternoon (3pm – 5pm)" },
  { value: "flexible", label: "Flexible — call to confirm" },
];

const Field = ({ label, required, children, hint }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60">
      {label}{required && <span className="text-[#FF3B30]"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <div className="mt-1 text-white/40 text-xs">{hint}</div>}
  </label>
);

const inputCls =
  "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#FF3B30] outline-none text-white text-base px-4 py-3 transition-colors placeholder:text-white/30";

export default function Build() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    build_type: BUILD_TYPES[0],
    modifications: "",
    budget: "",
    preferred_date: "",
    preferred_time: "flexible",
    notes: "",
  });
  const [state, setState] = useState({ status: "idle", error: null, id: null });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === "submitting") return;
    setState({ status: "submitting", error: null, id: null });
    try {
      const res = await fetch(`${API}/build-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setState({ status: "success", error: null, id: data.id });
    } catch (err) {
      setState({ status: "error", error: err.message || "Something went wrong", id: null });
    }
  };

  // Min date = today
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="page-build">
      <Seo
        path="/build"
        title="Build & Consultation — Modifications, Tunes & Full Builds"
        description="Tell us what you want done — single mods, full builds, performance tunes — and pick a time to talk it through. Every job is photo documented at Dirty Wrenches Automotive in Hermiston, OR."
      />

      <PageHeader
        code="// 06"
        kicker="Build & Consult"
        title={<>Tell us the build.<br />Pick a time to talk.</>}
        subtitle="From a single mod to a full build, send the details and pick a window — we'll call you to confirm the consultation. Every job we do is photo documented start to finish."
        testId="build-header"
      />

      {/* Photo-documented strip */}
      <section data-testid="photo-doc-strip" className="px-6 md:px-12 lg:px-24 py-10 border-b border-white/10 bg-[#0E0E0E]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { icon: Camera, t: "Every job photographed", d: "Before, during and after. You see what we did." },
            { icon: Wrench, t: "Same hands, every visit", d: "Owner-operated. No flat-rate hustle." },
            { icon: Clock, t: "Honest scheduling", d: "We book a couple weeks out — and we keep our slots." },
          ].map((b, i) => (
            <div key={b.t} data-testid={`photo-doc-card-${i}`} className="bg-[#0A0A0A] p-6 md:p-7 flex items-start gap-4">
              <div className="w-10 h-10 grid place-items-center bg-[#FF3B30]/15 text-[#FF3B30] shrink-0">
                <b.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold uppercase tracking-tight text-base md:text-lg">{b.t}</div>
                <div className="text-white/55 text-sm mt-1 leading-relaxed">{b.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// what to include</div>
              <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
                The more you tell us, the better.
              </h2>
              <ul className="mt-6 space-y-3 text-white/70 text-sm">
                {[
                  "Year, make, model and trim",
                  "What you want done — be specific",
                  "Brand preferences (Cobb, MHD, KW, etc.)",
                  "Budget range (helps us scope honestly)",
                  "Anything broken or unusual",
                  "Track / street use",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1 w-4 h-4 grid place-items-center bg-[#FF3B30]/15 text-[#FF3B30] shrink-0">
                      <Check className="w-2.5 h-2.5" strokeWidth={3} />
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#1A1F24] border border-white/10 p-6">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-[#FF3B30]" />
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60">Photo documented</div>
              </div>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                Every car that comes through the bay gets photographed at each stage — torn-down, mid-build, finished. You'll get the album with the invoice. No mystery, no "trust us."
              </p>
            </div>

            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60 mb-2">Prefer to call?</div>
              <a
                href={`tel:${SITE.contact.phone}`}
                data-testid="build-tel-link"
                className="font-display font-black uppercase tracking-tight text-2xl text-white hover:text-[#FF3B30] transition-colors"
              >
                {SITE.contact.phone}
              </a>
              <div className="mt-1 text-white/50 text-sm">{SITE.contact.address}</div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {state.status === "success" ? (
              <div data-testid="build-success" className="bg-[#0F1A12] border border-emerald-500/30 p-8 md:p-12">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-emerald-400">// request received</div>
                <h3 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                  Got it. We'll be in touch.
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed max-w-xl">
                  Thanks for the detail. We'll review the build, line up the parts research, and call you at the number you provided to confirm the consultation. Usually inside one business day.
                </p>
                <div className="mt-6 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/40">
                  Reference: <span className="text-white/70">{state.id}</span>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setState({ status: "idle", error: null, id: null });
                      setForm((f) => ({ ...f, modifications: "", notes: "" }));
                    }}
                    data-testid="build-new-btn"
                    className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 transition-colors"
                  >
                    Submit another
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 transition-colors"
                  >
                    Back to home <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <form data-testid="build-form" onSubmit={submit} className="bg-[#0E0E0E] border border-white/10 p-6 md:p-10 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name" required>
                    <input
                      data-testid="build-input-name"
                      className={inputCls}
                      value={form.name}
                      onChange={set("name")}
                      required
                      placeholder="First and last"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      data-testid="build-input-phone"
                      className={inputCls}
                      value={form.phone}
                      onChange={set("phone")}
                      required
                      placeholder="(541) 555-0199"
                      type="tel"
                    />
                  </Field>
                </div>

                <Field label="Email" required>
                  <input
                    data-testid="build-input-email"
                    className={inputCls}
                    value={form.email}
                    onChange={set("email")}
                    required
                    type="email"
                    placeholder="you@example.com"
                  />
                </Field>

                <Field label="Vehicle (year / make / model / trim)" required>
                  <input
                    data-testid="build-input-vehicle"
                    className={inputCls}
                    value={form.vehicle}
                    onChange={set("vehicle")}
                    required
                    placeholder="2018 BMW M3 Competition"
                  />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Build type" required>
                    <select
                      data-testid="build-input-type"
                      className={inputCls}
                      value={form.build_type}
                      onChange={set("build_type")}
                      required
                    >
                      {BUILD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                  <Field label="Budget range" hint="Optional — helps us scope honestly">
                    <input
                      data-testid="build-input-budget"
                      className={inputCls}
                      value={form.budget}
                      onChange={set("budget")}
                      placeholder="$2k – $5k"
                    />
                  </Field>
                </div>

                <Field
                  label="Modifications / build details"
                  required
                  hint="The more specific, the better. Brands, parts, goals, what's already done."
                >
                  <textarea
                    data-testid="build-input-mods"
                    className={`${inputCls} min-h-[160px] resize-y leading-relaxed`}
                    value={form.modifications}
                    onChange={set("modifications")}
                    required
                    placeholder="Example:
- MHD Stage 2 tune
- VRSF downpipes, BMS intake
- New plugs and coils
- Want it street-driveable, occasional track days"
                  />
                </Field>

                <div className="border-t border-white/10 pt-6">
                  <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase mb-4">// schedule a consult</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Preferred date" hint="We'll confirm by phone">
                      <input
                        data-testid="build-input-date"
                        type="date"
                        min={today}
                        className={inputCls}
                        value={form.preferred_date}
                        onChange={set("preferred_date")}
                      />
                    </Field>
                    <Field label="Preferred time">
                      <select
                        data-testid="build-input-time"
                        className={inputCls}
                        value={form.preferred_time}
                        onChange={set("preferred_time")}
                      >
                        {TIME_SLOTS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>

                <Field label="Anything else?" hint="Optional">
                  <textarea
                    data-testid="build-input-notes"
                    className={`${inputCls} min-h-[100px] resize-y leading-relaxed`}
                    value={form.notes}
                    onChange={set("notes")}
                    placeholder="Other context, deadlines, links to similar builds, etc."
                  />
                </Field>

                {state.status === "error" && (
                  <div data-testid="build-error" className="flex items-start gap-3 bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF8A82] p-4">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="text-sm">{state.error}</div>
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
                  <div className="text-white/45 text-xs max-w-md leading-relaxed">
                    By submitting you agree we can contact you at the phone and email above to schedule the consultation.
                  </div>
                  <button
                    data-testid="build-submit-btn"
                    type="submit"
                    disabled={state.status === "submitting"}
                    className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5722] disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
                  >
                    {state.status === "submitting" ? "Sending…" : "Send build & schedule consult"}
                    {state.status !== "submitting" && (
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
